import Checkbox from '@mui/material/Checkbox'
import { PostedPost as IPostedPost, POST_TYPE_POSTED } from 'interfaces'
import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Observable } from 'rxjs'
import { facebookBackgroundTemplatesSelector } from 'services/compose/selectors'
import { profilesByPpidSelector } from 'services/destinations'
import { BRAND_TWITTER } from 'shared'
import { noop } from 'utils/noop'
import PostBase from './PostBase'
import styles from './PostBase.pcss'
import PostedActionsMenu from './PostedActionsMenu'
import PostedStats from './PostedStats'
import { RecycleSelector } from './components/RecycleSelector'
import { RepeatIndicator } from './components/RepeatIndicator'

export interface PostedPostProps {
  post: IPostedPost
  selected: boolean
  withDate?: boolean
  onRequeue: (id: string, ppid: string) => void
  onRepost: (post: IPostedPost) => void
  onCopy: (post: IPostedPost) => void
  onShareNow?: (post: IPostedPost) => void
  onMediaPreviewBtnClick: (post: IPostedPost) => void
  onToggleSelected: (postId: string) => void
  onToggleFavorite?: (postId: string, ppid: string, value: boolean) => Observable<any>
  onArchive?: (postId: string, ppid: string) => void
}

export function PostedPost(props: PostedPostProps) {
  const { post, onRequeue, onRepost, onCopy, onShareNow, onToggleFavorite, onArchive, onMediaPreviewBtnClick, onToggleSelected } = props
  const profiles = useSelector(profilesByPpidSelector)
  const tobiTemplates = useSelector(facebookBackgroundTemplatesSelector)
  const profile = profiles[post.ppPageId]

  const postDetails = useMemo(() => {
    if (post.details?.textBackground && !post.details.textBackground.backgroundUrl) {
      const template = tobiTemplates.find((t) => t.id === post.details!.textBackground?.id)
      if (template) {
        return {
          ...post.details,
          textBackground: {
            ...post.details.textBackground,
            backgroundUrl: template.backgroundUrl
          }
        }
      }
    }
    return post.details
  }, [post.details, tobiTemplates])

  const handleMediaPreview = useCallback(() => {
    onMediaPreviewBtnClick(post)
  }, [onMediaPreviewBtnClick, post])

  const requeue = useCallback(() => {
    onRequeue(post.id, post.ppPageId)
  }, [onRequeue, post.id, post.ppPageId])

  const toggleSelected = useCallback(() => {
    onToggleSelected(post.id)
  }, [onToggleSelected, post.id])

  const repost = useCallback(() => {
    onRepost(post)
  }, [onRepost, post])

  const copy = useCallback(() => {
    onCopy(post)
  }, [onCopy, post])

  const shareNow = useCallback(() => {
    if (onShareNow) {
      onShareNow(post)
    }
  }, [onShareNow, post])

  const toggleFavorite = useCallback((value: boolean) => {
    if (onToggleFavorite) {
      return onToggleFavorite(post.id, post.ppPageId, value)
    }
    return new Observable(observer => observer.complete())
  }, [onToggleFavorite, post.id, post.ppPageId])

  const archive = useCallback(() => {
    if (onArchive) {
      onArchive(post.id, post.ppPageId)
    }
  }, [onArchive, post.id, post.ppPageId])

  const actions = useMemo(() => (
    <div className={styles['actions-posted']}>
      {props.post.repeatLength ? (
        <RepeatIndicator
          repeatLength={props.post.repeatLength}
          repeatPeriod={props.post.repeatPeriod}
          repeatUntil={props.post.repeatUntil}
        />
      ) : (
        <RecycleSelector
          value={post.recycle}
          className={styles.recycle}
          activeClassName={styles.on}
          disabled
          onChange={noop}
        />
      )}
      {post.network !== BRAND_TWITTER && (
        <PostedStats
          shares={post.sharesFormatted}
          likes={post.likesFormatted}
          comments={post.commentsFormatted}
          saves={post.savesFormatted}
          isRepin={post.isRepin}
          network={post.network}
          lastUpdated={post.insightsUpdated}
        />
      )}
      <div className={styles['posted-actions-btn-box']}>
        <PostedActionsMenu
          link={post.realLink}
          isFavorite={Boolean(post.isFavorite)}
          isBucketPost={Boolean(post.bucket)}
          isArchived={Boolean(post.isArchived)}
          onCopy={copy}
          onRepost={repost}
          onRequeue={requeue}
          onArchive={onArchive ? archive : undefined}
          onShareNow={props.onShareNow ? shareNow : undefined}
          onToggleFavorite={onToggleFavorite ? toggleFavorite : undefined}
        />
        <Checkbox
          color="primary"
          classes={{ root: styles.checkbox }}
          checked={props.selected}
          onChange={toggleSelected}
        />
      </div>
    </div>
  ), [
    props.post.repeatLength, props.post.repeatPeriod, post.isArchived,
    props.post.repeatUntil, props.onShareNow, props.selected, post.recycle, post.sharesFormatted,
    post.likesFormatted, post.commentsFormatted, archive, toggleFavorite,
    post.savesFormatted, post.isRepin, post.network, post.isFavorite,
    post.realLink, post.bucket, copy, repost, requeue, shareNow, toggleSelected,
    onArchive, onToggleFavorite, post.insightsUpdated
  ])

  return (
    <div className={styles.outerContainer}>
      <PostBase
        actions={actions}
        postId={post.id}
        postType={POST_TYPE_POSTED}
        contentType={post.contentType}
        text={post.status}
        images={post.images}
        timeFormatted={post.timeString}
        timezone={post.timezone}
        profileNetwork={post.network}
        profile={profile}
        title={post.linkTitle}
        description={post.linkDescription}
        linkUrl={post.linkUrl}
        nativeVideoUrl={post.nativeVideoUrl}
        fbVideoUrl={post.fbVideoUrl}
        specialType={post.specialType}
        twitterPostType={post.twPostType}
        linkDomain={post.linkDomain}
        onMediaPreviewBtnClick={handleMediaPreview}
        attributes={post.attributes}
        date={post.calendarDate}
        withDate={props.withDate}
        details={postDetails}
        bucket={post.bucket}
        comments={post.postComments}
        isPosted
      />
    </div>
  )
}

export default memo(PostedPost)
